import { React, useMemo, useState, useEffect } from "react";
import { Empty } from 'antd';
import '../../Styles/styles-misc.css';
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { addRestAndTravelDates } from '../Subjective/SubjectiveCharts.jsx'
import BarChartHighcharts from "../Charts/BarChart.jsx";
import {
    getRestAndTravelDaysInChart,
} from "../Team/TeamAvgCharts";
import { BodyMapView } from "../BodyMapView/BodyMapView";
import { LoadingComponent } from "../Common/LoadingComponent";
import { mapDataForHighcharts } from "../../mappers/model/BarChartMapper.js";
import { findNearestNumber } from "../../Utils/chart_utils";

export const WellnessChartWithSelect = ({ 
    pageType,
    wellnessMetric, 
    startDate,
    endDate,
    showRest=false, 
    showTravel=false,
    showBodyMap=true,
 }) => {
    const dispatch = useDispatch();
    const {
        games,
        trainings,
        filter: {
            showGameDays,
        },
        info: {display_name: teamName, team_colors: teamColors, restAndTravel, custom_charts: customChartData,
			acwr_threshold_config: acwrThresholdConfig, metric: teamMetric
		},
        filter: {
			showGameDays: teamShowGameDays,
			showPredictions: teamShowPredictions,
			showWeekly: teamShowWeekly,
			position,
			player_group,
			session: teamSession,
		},
        loading: teamLoading
    } = useSelector(({ team }) => team);
    const isTeamPage = pageType === 'team';
    const [wellnessChart, setWellnessChart] = useState();
    const [metricKey, setMetricKey] = useState();
    const [hasBodyMap, setHasBodyMap] = useState(false);
    const [chartLoading, setChartLoading] = useState(false);
    const [dataExists, setDataExists] = useState(false);
    const [chartData, setChartData] = useState();

    useEffect(() => {
        if (isTeamPage && teamLoading && Object.entries(teamLoading).length > 0 && 'gps' in teamLoading) {
            if (teamLoading.gps === true) {
                setChartLoading(true);
            } else {
                setChartLoading(false);
            }
        }
    }, [teamLoading])

    const gameDates = useMemo(
        () =>
            trainings?.data?.reduce(
                (cur, data) => ({ ...cur, [data.date]: data.opponent }),
                {}
            ),
        [trainings]
    );

    const filteredData = useMemo(() => (
            showGameDays && trainings?.data
            ? 
            {
                ...trainings,
                data: trainings?.data?.filter((data) => {
                    if (data.game_id) gameDates[data.date] = true;
                    return data.game_id;
                }),
                wellness: Object.keys(trainings?.wellness || {}).reduce(
                    (cur, key) => ({
                        ...cur,
                        [key]: trainings?.wellness?.[key]?.filter(
                            (data) => gameDates[data.date]
                        ),
                    }),
                    {}
                ),
            }
            : 
            trainings
        ),
        [trainings, gameDates, showGameDays]
    );

    const getAnswerByValue = (key, value) => {
        if (wellnessMetric && wellnessMetric.answers) {
            const [answer, index] = findNearestNumber(wellnessMetric.answers, value);
            if (answer) {
            return [answer.answer, index];
            }
        }
        return [null];
    };

    const getEmptyState = () =>  (
                    <div
                        style={{
                        width: 600,
                        height: 400,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        }}
                    >
                        <Empty description="No data in the specified date range" />
                    </div>
                )

    useEffect(() => {     
        let wellnessMetricKey;
        if (wellnessMetric && Object.keys(wellnessMetric).length > 0) {
            wellnessMetricKey = wellnessMetric.key;
            setMetricKey(wellnessMetricKey);
        }

        if (filteredData && Object.entries(filteredData).length > 0) {
            if (filteredData.hasOwnProperty('bodymap_data') && 
                filteredData.bodymap_data?.hasOwnProperty(wellnessMetricKey)
            ) {
                setHasBodyMap(true);
            } else {
                setHasBodyMap(false);
            }
            if ('wellness' in filteredData) {
                const wellnessData = filteredData["wellness"];
                if (wellnessData.hasOwnProperty(wellnessMetricKey) && wellnessData[wellnessMetricKey].length > 0) {
                    setDataExists(true);
                    setChartData(addRestAndTravelDates(
                        filteredData["wellness"][wellnessMetricKey].map((dt) => {
                        const [answerTxt, index] =
                            wellnessMetric?.answer_type?.toLowerCase() === "float" ||
                            wellnessMetric?.answer_type?.toLowerCase() === "int"
                            ? [dt[wellnessMetricKey], -1]
                            : getAnswerByValue(
                                wellnessMetricKey,
                                Math.round(dt[wellnessMetricKey])
                                );
                        const gameDay = games.find(
                            (game) => game.date === dt.date
                        );
                        return {
                            ...dt,
                            answerTxt,
                            answerIndex: index,
                            opponent: gameDay?.opponent,
                        };
                        }),
                        [wellnessMetricKey, "answerIndex"],
                        restAndTravelDates
                    ));
                } else {
                    setDataExists(false);
                    setWellnessChart(getEmptyState());
                } 
            } else {
                setDataExists(false);
                setWellnessChart(getEmptyState());
            } 
        } else {
            setDataExists(false);
            setWellnessChart(getEmptyState());
        }
    }, [filteredData, wellnessMetric])

    const restAndTravelDates = useMemo(
        () =>
            getRestAndTravelDaysInChart(
                startDate,
                endDate,
                Object.values(trainings.wellness || {})?.[0],
                restAndTravel,
                showRest,
                showTravel
            ),
        [startDate, endDate, restAndTravel, showRest, trainings, showTravel]
    );


    useEffect(() => {
        if (chartData && chartData.length > 0) {
            if (hasBodyMap) {
                setWellnessChart(
                    <>
                        <BarChartHighcharts 
                            className="wellness-bar-charts-select width-90" 
                            options={{
                            ...mapDataForHighcharts({
                                dataIn: {data: chartData},
                                metric: wellnessMetric,
                                teamName,
                                games,
                                teamColors,
                                singleAxis: true,
                                isSubjectiveMetric: true,
                                teamMetric,
                                acwrThresholdConfig,
                                showTitle: false
                            }),
                            chart:{
                                ...mapDataForHighcharts({
                                dataIn: {data: chartData},
                                metric: wellnessMetric,
                                teamName,
                                games,
                                teamColors,
                                singleAxis: true,
                                isSubjectiveMetric: true,
                                teamMetric,
                                acwrThresholdConfig,
                                showTitle: false
                                })?.chart,
                                height: '100%'
                            }
                            }}>
                        </BarChartHighcharts>
                        {
                            showBodyMap && (
                                <div
                                    style={{
                                        display: "flex",
                                        width: "100%",
                                        justifyContent: "fllex-start",
                                        alignItems: "center",
                                    }}
                                >
                                    <BodyMapView
                                        playersPage={false}
                                        title={`${wellnessMetric?.name} Map`}
                                        data={filteredData.bodymap_data[metricKey]}
                                        question_key={metricKey}
                                    />
                                </div>
                            )
                        }
                    </>
                );
            } else {
                setWellnessChart(
                    <BarChartHighcharts 
                        className="wellness-bar-charts"
                        options={{
                            ...mapDataForHighcharts({
                            dataIn: {data: chartData},
                            metric: wellnessMetric,
                            teamName,
                            teamColors,
                            games,
                            singleAxis: true,
                            isSubjectiveMetric: true,
                            teamMetric,
                            acwrThresholdConfig,
                            showTitle: false
                            }),
                            chart:{
                            ...mapDataForHighcharts({
                                dataIn: {data: chartData},
                                metric: wellnessMetric,
                                teamName,
                                teamColors,
                                games,
                                singleAxis: true,
                                isSubjectiveMetric: true,
                                teamMetric,
                                acwrThresholdConfig,
                                showTitle: false
                            })?.chart,
                            height: '100%'
                        }}}
                    >
                    </BarChartHighcharts>
                );
            }
        }
    }, [chartData])

    return (
        <>
            <LoadingComponent loading={chartLoading} title="Loading chart...">
                {wellnessChart}
            </LoadingComponent>
        </>
    );
};

export default WellnessChartWithSelect;